// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import { updateBundleMembers } from '@/bundle/api/update-bundle-members';
import {
  BundleMembersForm,
  BundleMembersFormRef,
} from '@/bundle/components/bundle-members-form';
import {
  bundleQueryKey,
  bundlesQueryKey,
  useBundle,
} from '@/bundle/hooks/use-bundles';
import { bundleMembersFormSchema } from '@/bundle/schemas/bundle-members';
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { SupportSnackbar } from '@/components/support-snackbar';
import { UnsavedChangesDialog } from '@/components/unsaved-changes-dialog';
import { AddRounded } from '@/constants/hardfin-standard-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Collapse, FormHelperText, Stack } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export const Route = createFileRoute('/_app/catalog/bundles/$bundleKey/')({
  component: BundleItemsRoute,
});

const formSchema = z.object({
  members: bundleMembersFormSchema,
});

type FormData = z.infer<typeof formSchema>;

/**
 * Maps a bundle's persisted members (in position order) to form rows.
 */
function toFormMembers(bundle: Bundle): FormData['members'] {
  return [...bundle.members]
    .sort((a, b) => a.position - b.position)
    .map((member) => ({
      item: member.item,
      quantity: member.defaultQty,
    }));
}

function BundleItemsRoute() {
  const { bundleKey } = Route.useParams();
  // Key the content by bundle so form state re-initializes when navigating
  // between bundles while this route stays mounted
  return <BundleItemsContent key={bundleKey} bundleKey={bundleKey} />;
}

/**
 * The bundle detail page's Items section.
 *
 * Edits accumulate in form state (adds, removes, reorders, and quantity
 * changes); nothing persists per row. When the form is dirty, a "Save
 * changes" button appears below the table, and one mutation persists the
 * entire member list. A bundle can never be saved empty.
 */
function BundleItemsContent(props: { bundleKey: string }) {
  const queryClient = useQueryClient();
  const formRef = useRef<BundleMembersFormRef>(null);

  const { bundle } = useBundle(props.bundleKey);

  const initialMembers = toFormMembers(bundle);

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      members: initialMembers,
    },
  });

  const { handleSubmit, formState, reset, resetField } = methods;
  const { isDirty } = formState;

  // Saving replaces the full member list, so a stale baseline would clobber
  // changes made elsewhere; re-seed from fresh query data while the user has
  // no in-progress edits (once dirty, last write wins on save)
  useEffect(() => {
    if (!isDirty) {
      reset({ members: toFormMembers(bundle) });
    }
  }, [bundle, isDirty, reset]);

  const blocker = useBlocker({ condition: isDirty });
  const isBlocked = blocker.status === 'blocked';

  // Revert to the form's default values, which reset() keeps in sync with
  // the last saved member list
  const handleClickReset = () => {
    resetField('members');
  };

  const updateMembersMutation = useMutation({
    mutationFn: (members: FormData['members']) => {
      const formattedMembers = members.flatMap((member) =>
        member.item === null
          ? []
          : [{ itemId: member.item.id, defaultQty: member.quantity }]
      );
      return updateBundleMembers(props.bundleKey, {
        members: formattedMembers,
      });
    },
    onSuccess: (responseData) => {
      queryClient.invalidateQueries({
        queryKey: bundleQueryKey(props.bundleKey),
      });
      queryClient.invalidateQueries({ queryKey: bundlesQueryKey });
      reset({ members: toFormMembers(responseData.bundle) });
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateMembersMutation.mutate(data.members);
  };

  const membersError =
    formState.errors.members?.root?.message ??
    formState.errors.members?.message;

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleValidSubmission)}>
        <SupportSnackbar
          isOpen={updateMembersMutation.isError}
          message="An error occurred while saving the bundle's items."
          onClose={updateMembersMutation.reset}
        />
        <UnsavedChangesDialog
          isOpen={isBlocked}
          onCancel={blocker.reset}
          onProceed={blocker.proceed}
        />
        <HardfinTabCardWrapper
          title="Items"
          actions={
            <Button
              variant="contained"
              color="secondary"
              onClick={() => formRef.current?.addMemberRow()}
              startIcon={<AddRounded fontSize="small" />}
            >
              Add item
            </Button>
          }
        >
          <Stack paddingTop={2}>
            <BundleMembersForm ref={formRef} name="members" />
            {membersError && (
              <FormHelperText error>{membersError}</FormHelperText>
            )}
            <Collapse in={isDirty}>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                paddingTop={2}
                paddingBottom={1}
              >
                <Button onClick={handleClickReset}>Reset</Button>
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={updateMembersMutation.isPending}
                >
                  Save changes
                </Button>
              </Stack>
            </Collapse>
          </Stack>
        </HardfinTabCardWrapper>
      </form>
    </FormProvider>
  );
}
